import { useCallback, useMemo, useState } from 'react';

const STORAGE_KEY = 'domaintriage-shortlist';

/**
 * Read the shortlist from localStorage, returning an empty set on failure.
 */
function loadShortlist(): Set<string> {
  if (typeof window === 'undefined') return new Set();

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((v): v is string => typeof v === 'string'));
    }
  } catch {
    // Corrupt data — start fresh.
  }
  return new Set();
}

/**
 * Persist the shortlist to localStorage.
 */
function saveShortlist(set: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // Storage full or unavailable — silently ignore.
  }
}

export interface UseShortlistReturn {
  /** The current set of shortlisted domains. */
  shortlist: Set<string>;
  /** Add the domain if absent, remove it if present. */
  toggle: (domain: string) => void;
  /** Remove a specific domain from the shortlist. */
  remove: (domain: string) => void;
  /** Remove all domains from the shortlist. */
  clear: () => void;
  /** Check whether a domain is on the shortlist. */
  has: (domain: string) => boolean;
  /** Number of domains on the shortlist. */
  count: number;
}

/**
 * Hook for managing a persisted shortlist of domain names.
 *
 * The shortlist is stored in `localStorage` under the key
 * `domaintriage-shortlist` as a JSON array. It is loaded once on mount and
 * written back on every mutation.
 */
export function useShortlist(): UseShortlistReturn {
  const [shortlist, setShortlist] = useState<Set<string>>(loadShortlist);

  const update = useCallback((next: Set<string>) => {
    setShortlist(next);
    saveShortlist(next);
  }, []);

  const toggle = useCallback(
    (domain: string) => {
      setShortlist((prev) => {
        const next = new Set(prev);
        if (next.has(domain)) {
          next.delete(domain);
        } else {
          next.add(domain);
        }
        saveShortlist(next);
        return next;
      });
    },
    [],
  );

  const remove = useCallback(
    (domain: string) => {
      setShortlist((prev) => {
        if (!prev.has(domain)) return prev;
        const next = new Set(prev);
        next.delete(domain);
        saveShortlist(next);
        return next;
      });
    },
    [],
  );

  const clear = useCallback(() => {
    update(new Set());
  }, [update]);

  const has = useCallback(
    (domain: string) => shortlist.has(domain),
    [shortlist],
  );

  const count = useMemo(() => shortlist.size, [shortlist]);

  return { shortlist, toggle, remove, clear, has, count };
}
